import { Component, inject, OnInit, OnDestroy, ViewEncapsulation, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { startOfMonth, endOfMonth, addMinutes, isBefore, parse, format, isValid, isSameDay, startOfDay, addMonths, isSameMonth, isEqual, isAfter } from 'date-fns';
import { takeUntil, Subject, map, Observable, startWith } from 'rxjs';
import { AppStateService } from '../app-state.service';
import { AppointmentTypeRequest, OperatoriesResponse, SlotsRequest, SlotsResponse } from '../registration/appointment.model';
import { AppointmentService } from '../registration/appointment.service';
import { ConfigService } from '../config.service';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { HeaderComponent } from '../header/header.component';
import { CustomCalendarHeaderComponent } from '../calender-header.component';
import { CalendarEventService } from '../calender-event.service';
import { SlotCacheService } from '../slot-cache.service';
import { DeeplinkService } from '../deeplink.service';

@Component({
  selector: 'app-new-patient-scheduler',
  templateUrl: './new-patient-scheduler.component.html',
  styleUrls: ['./new-patient-scheduler.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSelectModule,
    MatRadioModule,
    HeaderComponent,
    MatAutocompleteModule,
    CustomCalendarHeaderComponent
  ],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(100%)', opacity: 0 }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-out', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideUpScale', [
      transition(':enter', [
        style({ transform: 'scale(0.9)', opacity: 0 }),
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'scale(1)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'scale(0.95)', opacity: 0 }))
      ])
    ])
  ]
})
export class NewPatientSchedulerComponent implements OnInit, OnDestroy, AfterViewInit {
  clientId: string = '';
  appointmentForm!: FormGroup;
  isLoading = false;
  isGlobalLoading = false;
  availableSlots: any[] = [];
  displayedSlots: any = {};
  selectedSlot: any | null = null;
  operatories: any[] = [];
  appointmentDuration = 30;
  appState = inject(AppStateService);
  appointmentService = inject(AppointmentService);
  slotCacheService = inject(SlotCacheService);
  private unsubscribe$ = new Subject<void>();
  isDataReady = false;
  isScheduling = false;
  selectedDate: Date | null = null;
  userFriendlyDate: string | null = format(new Date(), 'MMMM d, yyyy');
  loadingStatus = 'Loading...';
  isDatepickerVisible = false;
  isAppointmentTypesLoading = false;
  isOperatoriesLoading = false;
  isSlotsLoading = false;
  isSubmitting = false;
  isDatepickerLoading = false;
  minDate: Date = new Date();
  maxDate: Date = addMonths(new Date(), 6);
  configService = inject(ConfigService);
  appConfig = this.configService.getConfigData() || {};
  filteredTreatments!: Observable<string[]>;
  treatments: string[] = [
    'Chipped tooth',
    'Toothache',
    'Knocked-out tooth',
    'Lost filling or crown',
    'Abscess',
    'Teeth cleaning',
    'Cavity filling',
    'Root canal',
    'Teeth whitening',
    'Gum treatment',
    'Wisdom teeth removal'
  ];

  showSuggestions = false;
  // Keep this FALSE - we use patient type dropdown instead of treatment description
  showTreatmentInput = false;
  calendarLoadingMonths: { [key: string]: boolean } = {};
  currentViewedMonth: string = this.formatMonthKey(new Date());
  customCalendarHeader = CustomCalendarHeaderComponent;
  calendarEventService = inject(CalendarEventService);
  @ViewChild('calendar') calendar: any;
  dropdownOpen = false;  // Custom dropdown state
  showConfirmation = false;  // Zocdoc-style confirmation modal

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private deeplinkService: DeeplinkService
  ) {
    this.clientId = this.appState.getState().clientId || '';
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFilteredTreatments();
    this.setupMonthSelectedSubscription();

    // Don't load appointment types automatically - wait for user to select patient type
  }

  private loadDefaultAppointmentTypes(): void {
    // Check if patient type is selected first
    const patientType = this.appointmentForm.get('patientType')?.value;
    if (!patientType) {
      return;
    }

    // Check if we have the necessary configuration before making the API call
    let orgTenantId = this.appState.getState().orgTenantId;

    // If not available, extract from current hostname as fallback
    if (!orgTenantId) {
      const hostname = window.location.hostname;
      if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        orgTenantId = hostname.split('.')[0];
      }
    }

    if (!orgTenantId) {
      return;
    }

    this.isLoading = true;
    this.isDataReady = false;
    this.loadingStatus = 'Loading available appointments...';
    this.isAppointmentTypesLoading = true;

    // Load appointment types based on selected patient type
    const backendValue = this.mapPatientTypeToBackend(patientType);
    const appointmentRequest: AppointmentTypeRequest = {
      treatmentRequest: backendValue
    };

    this.appointmentService.getAppointmentTypes(appointmentRequest)
      .subscribe({
        next: (response) => {
          this.isAppointmentTypesLoading = false;
          this.isLoading = false;
          if (response.length === 0) {
            this.snackBar.open('No appointment types available at this time.', 'Close', { duration: 5000 });
            return;
          }
          let appointmentType = response[0];
          if (appointmentType) {
            this.appointmentDuration = appointmentType.lengthInMinutes;
            this.appointmentForm.patchValue({ appointmentType: appointmentType.appointmentTypeNumber });
            this.loadAvailableOperatories(appointmentType.appointmentTypeName);
          }
        },
        error: (error) => {
          this.isAppointmentTypesLoading = false;
          this.isLoading = false;

          let errorMessage = 'Error loading appointment types. Please try again.';
          if (error.status === 0) {
            errorMessage = 'Connection error. Please check your internet connection.';
          } else if (error.status === 404) {
            errorMessage = 'Appointment service not available. Please try again later.';
          } else if (error.status === 500) {
            errorMessage = 'Server error occurred. Please contact support if this continues.';
          }

          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
  }

  ngOnDestroy(): void {
    // Clean up viewport lock if modal is open
    if (this.showConfirmation) {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    }

    this.slotCacheService.clearCache();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    // Automatically open the dropdown when component loads
    this.dropdownOpen = true;
  }

  private initializeForm() {
    this.appointmentForm = this.fb.group({
      patientType: [''],
      date: ['', Validators.required],
      time: ['', Validators.required],
      appointmentDescription: [''],
      appointmentType: ['']
    });
  }

  private setupFilteredTreatments() {
    this.filteredTreatments = this.appointmentForm.get('appointmentDescription')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
  }

  private setupMonthSelectedSubscription() {
    this.calendarEventService.monthSelected$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(date => this.onMonthSelected(date));
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.treatments.filter(option => option.toLowerCase().includes(filterValue));
  }

  hideSuggestions() {
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);  // Delay hiding to allow click event to register
  }

  onEnter(event: any) {
    if (event) {
      event.preventDefault();
    }
    this.onAppointmentDescriptionSubmit();
  }

  toggleDropdown() {
    if (!this.isSubmitting) {
      this.dropdownOpen = !this.dropdownOpen;
    }
  }

  selectPatientType(type: string) {
    this.appointmentForm.patchValue({ patientType: type });
    this.dropdownOpen = false;
    // Reload appointment types based on new patient type selection
    this.loadDefaultAppointmentTypes();
  }

  getSelectedPatientTypeLabel(): string {
    const value = this.appointmentForm.get('patientType')?.value;
    if (!value) {
      return 'Select patient type';
    }
    switch (value) {
      case 'new-emergency':
        return 'New patient emergency/other';
      case 'existing-emergency':
        return 'Existing patient emergency/other';
      case 'existing-treatment-plan':
        return 'Existing patient current treatment Plan';
      default:
        return 'Select patient type';
    }
  }

  // Map frontend values to backend strings
  private mapPatientTypeToBackend(patientType: string): string {
    switch (patientType) {
      case 'new-emergency':
        return 'New Patient';
      case 'existing-emergency':
        return 'Existing Patient Emergency';
      case 'existing-treatment-plan':
        return 'Existing patient current treatment Plan';
      default:
        return 'New Patient';
    }
  }

  formatMonthKey(date: Date | null): string {
    if (!date) {
      return '';
    }
    return format(date, 'yyyy-MM');
  }

  onAppointmentDescriptionSubmit() {
    const description = this.appointmentForm.get('appointmentDescription')?.value;
    if (description) {
      this.isLoading = true;
      this.isDataReady = false;
      this.loadingStatus = 'Loading appointment types...';
      this.isAppointmentTypesLoading = true;
      // Send the mapped patient type value to backend
      const patientType = this.appointmentForm.get('patientType')?.value;
      const backendValue = this.mapPatientTypeToBackend(patientType);
      const appointmentRequest: AppointmentTypeRequest = {
        treatmentRequest: backendValue
      };

      this.appointmentService.getAppointmentTypes(appointmentRequest)
        .subscribe(
          {
            next: (response) => {
              this.isAppointmentTypesLoading = false;
              this.isLoading = false;
              if (response.length === 0) {
                this.snackBar.open('No matching appointment type found.', 'Close', { duration: 5000 });
                return;
              }
              let appointmentType = response[0];
              if (appointmentType) {
                this.appointmentDuration = appointmentType.lengthInMinutes;
                this.appointmentForm.patchValue({ appointmentType: appointmentType.appointmentTypeNumber });
                this.loadAvailableOperatories(appointmentType.appointmentTypeName);
              } else {
                this.snackBar.open('No matching appointment type found. Please try a different description.', 'Close', { duration: 5000 });
              }
            },
            error: (error) => {
              this.isAppointmentTypesLoading = false;
              this.isLoading = false;

              let errorMessage = 'Error fetching appointment type. Please try again.';
              if (error.status === 0) {
                errorMessage = 'Connection error. Please check your internet connection.';
              } else if (error.status === 404) {
                errorMessage = 'Appointment service not available. Please try again later.';
              } else if (error.status === 500) {
                errorMessage = 'Server error occurred. Please contact support if this continues.';
              }

              this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
            }
          });
    }
  }

  loadAvailableOperatories(appointmentTypeName: string) {
    this.isLoading = true;
    this.loadingStatus = 'Loading available operatories...';
    this.isOperatoriesLoading = true;
    this.appointmentService.getMatchingOperatories({ appointmentTypeName })
      .subscribe(
        {
          next: (response: OperatoriesResponse) => {
            this.isOperatoriesLoading = false;
            this.operatories = response.operatories;
            if (this.operatories.length === 0) {
              this.snackBar.open('No operatories available for the selected appointment type', 'Close', { duration: 5000 });
              this.isLoading = false;
            } else {
              const startDate = this.adjustDateForTimezone(new Date());
              const operatories = this.operatories.map(operatory => operatory.operatoryNumber).join(',');
              this.loadAvailableSlots(startDate, operatories);
            }
          },
          error: () => {
            this.isOperatoriesLoading = false;
            this.isLoading = false;
            this.snackBar.open('Error fetching operatories. Please try again.', 'Close', { duration: 5000 });
          }
        });
  }

  loadAvailableSlots(startDate: Date, operatories: string) {
    const monthKey = format(startDate, 'yyyy-MM');
    const cachedSlots = this.slotCacheService.getCachedSlots(monthKey, operatories);
    if (cachedSlots) {
      this.availableSlots = cachedSlots;
      this.displayFirstAvailableDaySlots();
      this.isDatepickerVisible = true;
      this.isLoading = false;
      this.isDataReady = true;
      return;
    }

    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const endDate = endOfMonth(startDate);
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');

    let request: SlotsRequest = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      operatories,
      lengthInMinutes: this.appointmentDuration
    };

    this.isLoading = true;
    this.loadingStatus = 'Loading available slots...';
    this.isSlotsLoading = true;
    this.calendarLoadingMonths[monthKey] = true;

    this.appointmentService.getAvailableSlots(request)
      .subscribe(
        {
          next: (response: SlotsResponse) => {
            this.isSlotsLoading = false;
            this.calendarLoadingMonths[monthKey] = false;
            let slots = response.slots.map(slot => ({
              ...slot,
              date: this.adjustDateForTimezone(new Date(slot.date + 'T00:00:00')),
              start: new Date(slot.start),
              end: new Date(slot.end)
            }));

            // filter based on appointment duration
            slots = slots.filter(slot => slot.duration >= this.appointmentDuration);

            this.availableSlots = slots;
            this.slotCacheService.setCachedSlots(monthKey, operatories, slots);
            this.isDataReady = true;
            this.displayFirstAvailableDaySlots();
            this.isDatepickerVisible = true;
            this.isLoading = false;

            // this.prefetchNextMonthsSlots(startDate, operatories);
          },
          error: () => {
            this.isSlotsLoading = false;
            this.calendarLoadingMonths[monthKey] = false;
            this.isLoading = false;
            this.snackBar.open('Error fetching available slots. Please try again.', 'Close', { duration: 3000 });
          }
        });
  }


  prefetchNextMonthsSlots(currentStartDate: Date, operatories: string) {
    const nextMonth = addMonths(currentStartDate, 1);
    const monthAfterNext = addMonths(currentStartDate, 2);

    this.isDatepickerLoading = true;
    this.loadAvailableSlotsForMonth(nextMonth, 'nextMonth', operatories);
    this.loadAvailableSlotsForMonth(monthAfterNext, 'monthAfterNext', operatories);
  }

  loadAvailableSlotsForMonth(monthStart: Date, monthKey: string, operatories: string) {
    const cachedSlots = this.slotCacheService.getCachedSlots(monthKey, operatories);
    if (cachedSlots) {
      if (monthKey === this.currentViewedMonth) {
        this.availableSlots = cachedSlots;
        this.displayFirstAvailableDaySlots();
        if (this.calendar) {
          this.calendar.updateTodaysDate();
        }
      }
      return;  // Skip loading if already cached
    }

    const startDate = startOfMonth(monthStart);
    const endDate = endOfMonth(monthStart);

    const requestPayload: SlotsRequest = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      operatories,
      lengthInMinutes: this.appointmentDuration
    };

    this.calendarLoadingMonths[monthKey] = true;

    this.appointmentService.getAvailableSlots(requestPayload)
      .subscribe(
        {
          next: (response: SlotsResponse) => {
            let slots = response.slots.map(slot => ({
              ...slot,
              date: this.adjustDateForTimezone(new Date(slot.date + 'T00:00:00')),
              start: new Date(slot.start),
              end: new Date(slot.end)
            }));

            slots = slots.filter(slot => slot.duration >= this.appointmentDuration);

            this.slotCacheService.setCachedSlots(monthKey, operatories, slots);
            this.calendarLoadingMonths[monthKey] = false;
            this.isDatepickerLoading = false;
            if (monthKey === this.currentViewedMonth) {
              this.availableSlots = slots;
              this.displayFirstAvailableDaySlots();
              if (this.calendar) {
                this.calendar.updateTodaysDate();
              }

            }
          },
          error: () => {
            this.snackBar.open('Error fetching available slots. Please try again.', 'Close', { duration: 3000 });
            this.calendarLoadingMonths[monthKey] = false;
            this.isDatepickerLoading = false;
          }
        });
  }


  displayFirstAvailableDaySlots() {
    if (this.availableSlots.length > 0) {
      const firstAvailableDay = this.availableSlots[0].date;
      if (isValid(firstAvailableDay)) {
        this.selectedDate = firstAvailableDay;
        this.appointmentForm.patchValue({ date: firstAvailableDay });
        this.updateDisplayedSlots(firstAvailableDay);
      }
    }
  }

  adjustDateForTimezone(date: Date): Date {
    // Return the date as-is without timezone adjustment
    // The date string from API (e.g., "2026-06-01T00:00:00") should be treated as local date
    return new Date(date);
  }

  updateDisplayedSlots(date: Date) {
    if (isValid(date)) {
      const selectedDate = startOfDay(date);
      this.selectedSlot = null;
      this.userFriendlyDate = format(selectedDate, 'MMMM d, yyyy');
      this.appointmentForm.patchValue({ time: '' });

      // Close confirmation popup when slots change (date changed)
      this.showConfirmation = false;

      const slotsOnSelectedDate = this.availableSlots.filter(slot => {
        const slotDate = startOfDay(slot.date);
        return isSameDay(slotDate, selectedDate);
      });

      this.displayedSlots = slotsOnSelectedDate.reduce((result: any, slot: any) => {
        const operatory = slot.operatory;
        if (!result[operatory]) {
          result[operatory] = [];
        }
        const startTimes = this.generateStartTimes(slot);
        result[operatory].push(...startTimes);

        // Sort slots by start time and remove duplicates
        if (result[operatory].length > 0) {
          result[operatory] = result[operatory]
            .sort((a: any, b: any) => a.start.getTime() - b.start.getTime())
            .filter((slot: any, index: number, array: any[]) => {
              if (index === 0) return true;
              // Remove slots that start before the previous slot ends
              return slot.start.getTime() >= array[index - 1].end.getTime();
            });
        }

        return result;
      }, {});
    }
  }

  generateStartTimes(slot: any): any[] {
    const startTimes: any[] = [];
    let startTime = new Date(slot.start);
    const endTime = new Date(slot.end);

    if (isBefore(endTime, addMinutes(startTime, this.appointmentDuration))) {
      return startTimes;
    }

    while (isBefore(startTime, endTime) || isEqual(startTime, endTime)) {
      const slotEndTime = addMinutes(startTime, this.appointmentDuration);
      if (isBefore(slotEndTime, endTime) || isEqual(slotEndTime, endTime)) {
        startTimes.push({
          start: new Date(startTime),
          end: slotEndTime,
          operatory: slot.operatory
        });
      }
      startTime = addMinutes(startTime, this.appointmentDuration);
      if (isAfter(startTime, endTime)) {
        break;
      }
    }
    return startTimes;
  }

  // Validation method to check if both date and time are selected
  canShowScheduleAppointment(): boolean {
    // Use the same lenient validation as the dashboard scheduler
    // Just check if we have a selected date and slot, without strict form validation
    const hasSelectedDate = this.selectedDate !== null;
    const hasSelectedSlot = this.selectedSlot !== null;

    // Also ensure the form is not in a loading or submitting state
    const isNotBusy = !this.isSubmitting && !this.isGlobalLoading;

    return hasSelectedDate && hasSelectedSlot && isNotBusy;
  }

  selectSlot(slot: any, _event?: Event) {
    if (isValid(slot.start)) {
      // If the same slot is clicked again, deselect it
      if (this.selectedSlot === slot) {
        this.selectedSlot = null;
        this.appointmentForm.patchValue({ time: '' });
        this.showConfirmation = false;
      } else {
        this.selectedSlot = slot;
        this.appointmentForm.patchValue({ time: format(slot.start, 'HH:mm') });
        this.showConfirmation = false;
      }
    }
  }

  showBookingConfirmation() {
    this.showConfirmation = true;

    // Lock viewport scroll - modal should appear as popup overlay
    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');

    // Force modal to be visible in current viewport by scrolling it into view
    setTimeout(() => {
      const overlay = document.querySelector('[data-modal-overlay]') as HTMLElement;
      if (overlay) {
        // Scroll the overlay into view immediately (no smooth scroll)
        overlay.scrollIntoView({ behavior: 'auto', block: 'start' });
        // Alternative: just scroll to top of document
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 0);
  }

  hideBookingConfirmation() {
    // Restore viewport
    document.documentElement.classList.remove('modal-open');
    document.body.classList.remove('modal-open');

    this.showConfirmation = false;
    // Keep slot selected - user might want to confirm again
    // Only clear if they select a different slot or date
  }

  confirmAppointment() {
    // Call the existing saveAppointment method
    this.saveAppointment();
  }

  saveAppointment() {
    if (this.appointmentForm.valid) {
      const appointmentDate = this.appointmentForm.value.date;
      const appointmentTime = this.appointmentForm.value.time;
      const patientType = this.appointmentForm.value.patientType;
      const isNewPatient = patientType === 'new-emergency';
      if (isValid(appointmentDate) && appointmentTime) {
        const appointmentDateTime = new Date(`${format(appointmentDate, 'yyyy-MM-dd')}T${appointmentTime}:00`);
        this.isScheduling = true;
        this.isGlobalLoading = true;
        this.isSubmitting = true;

        const appointmentData: any = {
          appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
          isNewPatient: isNewPatient,
          patientType: patientType,
          appointmentTypeNumber: this.appointmentForm.value.appointmentType,
          summary: this.appointmentForm.value.appointmentDescription,
          operatoryNumber: this.operatories[0].operatoryNumber,
          lengthInMinutes: this.appointmentDuration
        };
        this.appState.setState({ appointmentData });
        this.slotCacheService.clearCache();

        // Clear deep link context before navigating to login
        this.deeplinkService.clearDeeplinkContext();

        // Always go to login page first
        // For new patients: login will redirect to registration if patient doesn't exist
        // For existing patients: login will authenticate and auto-book
        this.router.navigate(['/login']);
      }
    }
  }

  dateChanged(event: any) {
    this.selectedDate = new Date(event);
    this.appointmentForm.patchValue({ date: this.selectedDate });
    this.updateDisplayedSlots(this.selectedDate);

    // Reset time selection when date changes to ensure button hides properly
    this.selectedSlot = null;
    this.appointmentForm.patchValue({ time: '' });

    // Close any open confirmation popup when date changes
    this.showConfirmation = false;
  }

  onViewChange(event: any) {
    if (event.currentView === 'month') {
      const selectedMonth = startOfMonth(event.date);
      const monthKey = this.formatMonthKey(selectedMonth);
      this.currentViewedMonth = monthKey;  // Update the currently viewed month
      const operatories = this.operatories.map(operatory => operatory.operatoryNumber).join(',')
      if (!this.slotCacheService.getCachedSlots(monthKey, operatories)) {
        this.isDatepickerLoading = true;
        this.loadAvailableSlotsForMonth(selectedMonth, monthKey, operatories);
      } else {
        this.availableSlots = this.slotCacheService.getCachedSlots(monthKey, operatories) || [];
        this.updateDisplayedSlots(this.selectedDate || new Date());
      }
    }
  }

  onMonthSelected(date: Date): void {
    const selectedMonth = startOfMonth(date);
    const monthKey = this.formatMonthKey(selectedMonth);

    this.currentViewedMonth = monthKey;  // Update the currently viewed month
    this.displayedSlots = {};
    this.userFriendlyDate = null;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.showConfirmation = false; // Close any open confirmation popup
    this.appointmentForm.patchValue({ date: null, time: '' });

    const operatories = this.operatories.map(operatory => operatory.operatoryNumber).join(',');

    if (!operatories) {
      return;
    }

    if (!this.slotCacheService.getCachedSlots(monthKey, operatories)) {
      this.isDatepickerLoading = true;
      this.loadAvailableSlotsForMonth(selectedMonth, monthKey, operatories);
    } else {
      this.availableSlots = this.slotCacheService.getCachedSlots(monthKey, operatories) || [];
      this.displayFirstAvailableDaySlots();
    }
  }



  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view): string => {
    if (view === 'month') {
      if (isValid(cellDate)) {
        const formattedDate = format(cellDate, 'yyyy-MM-dd');
        const isAvailable = this.availableSlots.some(slot => format(slot.date, 'yyyy-MM-dd') === formattedDate);
        return isAvailable ? 'available-slot' : '';
      }
    }
    return '';
  }

  generateTimeOptions(slot: any): string[] {
    const options: string[] = [];
    let startTime = new Date(slot.start);
    const endTime = new Date(slot.end);
    while (isBefore(startTime, endTime)) {
      options.push(format(startTime, 'HH:mm'));
      startTime = addMinutes(startTime, 30);
    }
    return options;
  }

  goBack() {
    this.router.navigate(['/dashboard']);
  }

  getObjectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }
    return Object.keys(obj);
  }

  isMonthLoading(): boolean {
    const monthKey = this.currentViewedMonth;
    return this.calendarLoadingMonths[monthKey] || false;
  }

  // TrackBy function to prevent unnecessary re-renders when rooms update
  trackByOperatory(index: number, operatory: string): string {
    return operatory;
  }

  // TrackBy function for slot chips
  trackBySlot(index: number, slot: any): string {
    return `${slot.operatory}-${slot.start.getTime()}`;
  }

}
